import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { DataGrid } from '../common/datagrid/ResponsiveDataGrid';
import AddIcon from '@mui/icons-material/Add';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import SaveIcon from '@mui/icons-material/Save';
import SyncIcon from '@mui/icons-material/Sync';
import dayjs from 'dayjs';

const getYmOptions = () => {
  const options = [];
  const now = dayjs();
  for (let y = now.year() + 1; y >= now.year() - 1; y--) {
    for (let m = 12; m >= 1; m--) {
      options.push(`${y}${String(m).padStart(2, '0')}`);
    }
  }
  return options;
};

const toDateTimeLocal = (value) => {
  if (!value) return '';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DDTHH:mm') : '';
};

const toApiDateTime = (value) => {
  if (!value) return '';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : '';
};

const getMonthStart = (workYm) => dayjs(`${workYm}01`).format('YYYY-MM-DD');

const getMonthEnd = (workYm) => dayjs(`${workYm}01`).endOf('month').format('YYYY-MM-DD');

const buildDateTime = (workDt, timeValue, nextDay) => {
  const base = dayjs(`${workDt} ${timeValue || '00:00'}`);
  return (nextDay ? base.add(1, 'day') : base).format('YYYY-MM-DD HH:mm:ss');
};

const applyShiftToRow = (row, shift) => {
  if (!shift) return row;

  return {
    ...row,
    shiftId: shift.shiftId,
    shiftCd: shift.shiftCd,
    shiftNm: shift.shiftNm,
    startTm: shift.startTm,
    endTm: shift.endTm,
    endNextDayYn: shift.endNextDayYn,
    breakMin: shift.breakMin,
    workMin: shift.workMin,
    checkInGraceMin: shift.checkInGraceMin,
    checkOutGraceMin: shift.checkOutGraceMin,
    startDtm: buildDateTime(row.workDt, shift.startTm, false),
    endDtm: buildDateTime(row.workDt, shift.endTm, shift.endNextDayYn === 'Y'),
    scheduleStatus: 'CONFIRMED',
    sourceTyp: 'SELF',
    lockYn: row.lockYn || 'N',
  };
};

const WorkScheduleTab = ({
  list,
  shifts,
  holidays = [],
  loading,
  saving,
  searchYm,
  onSearchYmChange,
  onSave,
  onHolidaySave,
  onHolidayCollect,
  onChangeRequest,
}) => {
  const [rows, setRows] = useState([]);
  const [rangeStartDt, setRangeStartDt] = useState(getMonthStart(searchYm));
  const [rangeEndDt, setRangeEndDt] = useState(getMonthEnd(searchYm));
  const [rangeShiftId, setRangeShiftId] = useState('');
  const [rangeStartTm, setRangeStartTm] = useState('09:00');
  const [rangeEndTm, setRangeEndTm] = useState('18:00');
  const [rangeNextDay, setRangeNextDay] = useState(false);
  const [rangeExcludeHolidays, setRangeExcludeHolidays] = useState(true);
  const [holidayDt, setHolidayDt] = useState(dayjs().format('YYYY-MM-DD'));
  const [holidayNm, setHolidayNm] = useState('');
  const [holidayOffYn, setHolidayOffYn] = useState(true);

  useEffect(() => {
    setRows(list || []);
  }, [list]);

  useEffect(() => {
    setRangeStartDt(getMonthStart(searchYm));
    setRangeEndDt(getMonthEnd(searchYm));
    setHolidayDt(getMonthStart(searchYm));
  }, [searchYm]);

  const shiftMap = useMemo(() => {
    const map = {};
    (shifts || []).forEach((shift) => {
      map[shift.shiftId] = shift;
    });
    return map;
  }, [shifts]);

  const dayShift = useMemo(
    () => (shifts || []).find((shift) => shift.shiftCd === 'DAY'),
    [shifts],
  );
  const offShift = useMemo(
    () => (shifts || []).find((shift) => shift.shiftCd === 'OFF'),
    [shifts],
  );
  const holidayMap = useMemo(() => {
    const map = {};
    (holidays || []).forEach((holiday) => {
      if (holiday.offYn !== 'Y') return;
      const key = dayjs(holiday.holidayDt).format('YYYY-MM-DD');
      map[key] = map[key] ? `${map[key]}, ${holiday.holidayNm}` : holiday.holidayNm;
    });
    return map;
  }, [holidays]);

  const isOffDate = (workDt) => {
    const workDate = dayjs(workDt);
    return workDate.day() === 0 || workDate.day() === 6 || !!holidayMap[workDate.format('YYYY-MM-DD')];
  };

  useEffect(() => {
    if (!rangeShiftId && dayShift) {
      setRangeShiftId(dayShift.shiftId);
      setRangeStartTm(dayShift.startTm || '09:00');
      setRangeEndTm(dayShift.endTm || '18:00');
      setRangeNextDay(dayShift.endNextDayYn === 'Y');
    }
  }, [dayShift, rangeShiftId]);

  const updateRow = (workDt, patch) => {
    setRows((prev) =>
      prev.map((row) =>
        row.workDt === workDt ? { ...row, ...patch, scheduleStatus: 'CONFIRMED' } : row,
      ),
    );
  };

  const handleShiftChange = (row, shiftId) => {
    const next = applyShiftToRow(row, shiftMap[shiftId]);
    setRows((prev) => prev.map((item) => (item.workDt === row.workDt ? next : item)));
  };

  const handleRangeShiftChange = (shiftId) => {
    const shift = shiftMap[shiftId];
    setRangeShiftId(shiftId);
    if (shift) {
      setRangeStartTm(shift.startTm || '09:00');
      setRangeEndTm(shift.endTm || '18:00');
      setRangeNextDay(shift.endNextDayYn === 'Y');
    }
  };

  const handleApplyRange = () => {
    const shift = shiftMap[rangeShiftId];
    const start = dayjs(rangeStartDt);
    const end = dayjs(rangeEndDt);
    if (!shift || !start.isValid() || !end.isValid()) return;

    setRows((prev) =>
      prev.map((row) => {
        const workDate = dayjs(row.workDt);
        if (workDate.isBefore(start, 'day') || workDate.isAfter(end, 'day')) {
          return row;
        }
        if (row.scheduleStatus === 'CHANGE_REQUESTED') {
          return row;
        }
        if (rangeExcludeHolidays && (isOffDate(row.workDt) || row.leaveOffYn === 'Y')) {
          return row;
        }

        return {
          ...applyShiftToRow(row, shift),
          startTm: rangeStartTm,
          endTm: rangeEndTm,
          endNextDayYn: rangeNextDay ? 'Y' : 'N',
          startDtm: buildDateTime(row.workDt, rangeStartTm, false),
          endDtm: buildDateTime(row.workDt, rangeEndTm, rangeNextDay),
        };
      }),
    );
  };

  const handleFillDefault = () => {
    if (!dayShift && !offShift) return;

    setRows((prev) =>
      prev.map((row) => {
        if (row.scheduleStatus === 'CHANGE_REQUESTED') {
          return row;
        }
        const shift = (isOffDate(row.workDt) || row.leaveOffYn === 'Y')
          ? offShift || dayShift
          : dayShift || offShift;
        return applyShiftToRow(row, shift);
      }),
    );
  };

  const handleHolidaySave = async () => {
    if (!onHolidaySave || !holidayDt || !holidayNm.trim()) return;

    await onHolidaySave({
      holidayDt,
      holidayNm: holidayNm.trim(),
      holidayTyp: 'COMPANY',
      offYn: holidayOffYn ? 'Y' : 'N',
    });
    setHolidayNm('');
  };

  const handleSave = () => {
    onSave(rows.map((row) => ({
      ...row,
      startDtm: toApiDateTime(row.startDtm),
      endDtm: toApiDateTime(row.endDtm),
      scheduleStatus: row.scheduleStatus || 'CONFIRMED',
      sourceTyp: row.sourceTyp || 'SELF',
      lockYn: row.lockYn || 'N',
    })));
  };

  const columns = [
    {
      field: 'workDt',
      headerName: '근무일',
      minWidth: 130,
      flex: 0.7,
      valueFormatter: (value) => value ? dayjs(value).format('YYYY-MM-DD') : '-',
    },
    {
      field: 'dayOfWeek',
      headerName: '요일',
      width: 70,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (_, row) => dayjs(row.workDt).format('ddd'),
    },
    {
      field: 'holiday',
      headerName: '휴일',
      minWidth: 130,
      flex: 0.7,
      renderCell: ({ row }) => {
        const holidayName = holidayMap[dayjs(row.workDt).format('YYYY-MM-DD')];
        return holidayName ? (
          <Chip size="small" color="error" variant="outlined" label={holidayName} />
        ) : null;
      },
    },
    {
      field: 'leaveNm',
      headerName: '휴가/오프',
      minWidth: 130,
      flex: 0.7,
      renderCell: ({ row }) => (
        row.leaveNm ? (
          <Chip
            size="small"
            color={row.leaveOffYn === 'Y' ? 'warning' : 'info'}
            variant="outlined"
            label={row.leaveNm}
          />
        ) : null
      ),
    },
    {
      field: 'shiftId',
      headerName: '근무유형',
      minWidth: 150,
      flex: 0.8,
      renderCell: ({ row }) => (
        <TextField
          select
          size="small"
          value={row.shiftId || ''}
          onChange={(event) => handleShiftChange(row, event.target.value)}
          disabled={row.scheduleStatus === 'CHANGE_REQUESTED'}
          sx={{ width: '100%' }}
        >
          {(shifts || []).map((shift) => (
            <MenuItem key={shift.shiftId} value={shift.shiftId}>
              {shift.shiftNm}
            </MenuItem>
          ))}
        </TextField>
      ),
    },
    {
      field: 'startDtm',
      headerName: '예정 출근',
      minWidth: 190,
      flex: 1,
      renderCell: ({ row }) => (
        <TextField
          type="datetime-local"
          size="small"
          value={toDateTimeLocal(row.startDtm)}
          onChange={(event) => updateRow(row.workDt, { startDtm: toApiDateTime(event.target.value) })}
          disabled={row.scheduleStatus === 'CHANGE_REQUESTED'}
          sx={{ width: '100%' }}
          InputLabelProps={{ shrink: true }}
        />
      ),
    },
    {
      field: 'endDtm',
      headerName: '예정 퇴근',
      minWidth: 190,
      flex: 1,
      renderCell: ({ row }) => (
        <TextField
          type="datetime-local"
          size="small"
          value={toDateTimeLocal(row.endDtm)}
          onChange={(event) => updateRow(row.workDt, { endDtm: toApiDateTime(event.target.value) })}
          disabled={row.scheduleStatus === 'CHANGE_REQUESTED'}
          sx={{ width: '100%' }}
          InputLabelProps={{ shrink: true }}
        />
      ),
    },
    {
      field: 'scheduleStatus',
      headerName: '상태',
      width: 110,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <Chip
          size="small"
          color={row.scheduleStatus === 'CHANGE_REQUESTED' ? 'warning' : row.persistedYn === 'Y' ? 'success' : 'default'}
          variant={row.scheduleStatus === 'CHANGE_REQUESTED' ? 'outlined' : row.persistedYn === 'Y' ? 'filled' : 'outlined'}
          label={row.scheduleStatus === 'CHANGE_REQUESTED' ? '변경요청' : row.persistedYn === 'Y' ? '저장됨' : '신규'}
        />
      ),
    },
    {
      field: 'rmk',
      headerName: '비고',
      minWidth: 180,
      flex: 1,
      renderCell: ({ row }) => (
        <TextField
          size="small"
          value={row.rmk || ''}
          onChange={(event) => updateRow(row.workDt, { rmk: event.target.value })}
          disabled={row.scheduleStatus === 'CHANGE_REQUESTED'}
          sx={{ width: '100%' }}
        />
      ),
    },
    {
      field: 'requestActions',
      headerName: '',
      minWidth: 110,
      sortable: false,
      renderCell: ({ row }) => (
        <Tooltip title={row.persistedYn !== 'Y' ? '저장된 근무표만 변경 요청할 수 있습니다.' : '근무표 변경 요청'}>
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditCalendarIcon />}
              disabled={loading || saving || row.persistedYn !== 'Y' || row.scheduleStatus === 'CHANGE_REQUESTED'}
              onClick={() => onChangeRequest?.(row)}
            >
              요청
            </Button>
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
        <TextField
          select
          size="small"
          label="기준월"
          value={searchYm}
          onChange={(event) => onSearchYmChange(event.target.value)}
          sx={{ width: 140 }}
        >
          {getYmOptions().map((ym) => (
            <MenuItem key={ym} value={ym}>
              {ym.slice(0, 4)}년 {parseInt(ym.slice(4), 10)}월
            </MenuItem>
          ))}
        </TextField>

        <Box sx={{ flex: 1 }} />

        <Button
          variant="contained"
          size="small"
          startIcon={<SaveIcon />}
          disabled={loading || saving}
          onClick={handleSave}
        >
          저장
        </Button>
      </Stack>

      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}
      >
        <TextField
          type="date"
          size="small"
          label="시작일"
          value={rangeStartDt}
          onChange={(event) => setRangeStartDt(event.target.value)}
          sx={{ width: 155 }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          type="date"
          size="small"
          label="종료일"
          value={rangeEndDt}
          onChange={(event) => setRangeEndDt(event.target.value)}
          sx={{ width: 155 }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          select
          size="small"
          label="근무유형"
          value={rangeShiftId}
          onChange={(event) => handleRangeShiftChange(event.target.value)}
          sx={{ width: 150 }}
        >
          {(shifts || []).map((shift) => (
            <MenuItem key={shift.shiftId} value={shift.shiftId}>
              {shift.shiftNm}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          type="time"
          size="small"
          label="출근"
          value={rangeStartTm}
          onChange={(event) => setRangeStartTm(event.target.value)}
          sx={{ width: 120 }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          type="time"
          size="small"
          label="퇴근"
          value={rangeEndTm}
          onChange={(event) => setRangeEndTm(event.target.value)}
          sx={{ width: 120 }}
          InputLabelProps={{ shrink: true }}
        />
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={rangeNextDay}
              onChange={(event) => setRangeNextDay(event.target.checked)}
            />
          )}
          label="익일퇴근"
          sx={{ mr: 0 }}
        />
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={rangeExcludeHolidays}
              onChange={(event) => setRangeExcludeHolidays(event.target.checked)}
            />
          )}
          label="휴일 제외"
          sx={{ mr: 0 }}
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={<CalendarMonthIcon />}
          disabled={loading || saving || !rangeShiftId}
          onClick={handleApplyRange}
        >
          기간 적용
        </Button>
        <Tooltip title="선택월 전체를 평일 일근, 주말/휴일 휴무로 적용합니다.">
          <span>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CalendarMonthIcon />}
              disabled={loading || saving || (!dayShift && !offShift)}
              onClick={handleFillDefault}
            >
              월 기본값 적용
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}
      >
        <TextField
          type="date"
          size="small"
          label="휴일일자"
          value={holidayDt}
          onChange={(event) => setHolidayDt(event.target.value)}
          sx={{ width: 155 }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          label="휴일명"
          value={holidayNm}
          onChange={(event) => setHolidayNm(event.target.value)}
          sx={{ width: 180 }}
        />
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={holidayOffYn}
              onChange={(event) => setHolidayOffYn(event.target.checked)}
            />
          )}
          label="휴무"
          sx={{ mr: 0 }}
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          disabled={loading || saving || !holidayDt || !holidayNm.trim()}
          onClick={handleHolidaySave}
        >
          휴일 등록
        </Button>
        <Tooltip title="선택월의 국가공휴일을 공공데이터에서 가져와 반영합니다.">
          <span>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SyncIcon />}
              disabled={loading || saving || !onHolidayCollect}
              onClick={onHolidayCollect}
            >
              국가공휴일 가져오기
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.workDt}
        loading={loading}
        autoHeight
        density="compact"
        rowHeight={58}
        disableRowSelectionOnClick
        sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
      />
    </Box>
  );
};

export default WorkScheduleTab;
